import Logo from '@assets/logo.svg?react';
import { CompanySwitcher } from '@components/CompanySwitcher';
import { OddButton } from '@components/OddButton';
import {
	AccountsIcon,
	DashboardIcon,
	MessagesIcon,
	OperationIcon,
	SettingsIcon,
	SupportIcon,
	TransferIcon,
} from '@gurupay/gp-ui-library';
import { useUnreadThreadsCount } from '@hooks/useUnreadThreadsCount.ts';
import clsx from 'clsx';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { LogoutMenuItem } from './components/LogoutMenuItem';
import { MenuLanguageSelector } from './components/MenuLanguageSelector';

const menuItems = [
	{
		link: '/dashboard',
		labelKey: 'layout.controlPanel.dashboard',
		Icon: DashboardIcon,
	},
	{
		link: '/accounts',
		labelKey: 'layout.controlPanel.accounts',
		Icon: AccountsIcon,
	},
	{
		link: '/payments',
		labelKey: 'layout.controlPanel.payments',
		Icon: TransferIcon,
	},
	{
		link: '/operations',
		labelKey: 'layout.controlPanel.operations',
		Icon: OperationIcon,
	},
];

const menuBottom = [
	{
		link: '/support',
		labelKey: 'layout.help',
		Icon: SupportIcon,
	},
	{
		link: '/settings',
		labelKey: 'layout.controlPanel.settings',
		Icon: SettingsIcon,
	},
];

interface MenuProps {
	className?: string;
	onLinkClick: () => void;
}

export const Menu: FC<MenuProps> = ({ className, onLinkClick }) => {
	const { t } = useTranslation();
	const location = useLocation();
	const { data: unreadThreadsCount } = useUnreadThreadsCount();

	const isActive = (route: string) => location.pathname.startsWith(route);

	return (
		<div
			className={clsx(
				'flex flex-col h-[100vh] overflow-auto bg-neutral-50 gap-space-24',
				className,
			)}
		>
			<Link to="/">
				<Logo className="flex-[50px] flex-shrink-0 flex-grow-0" />
			</Link>
			<CompanySwitcher onCompanySelect={onLinkClick} />
			<menu className="flex-grow space-y-space-4">
				<OddButton />
				{menuItems.map(({ link, labelKey, Icon }) => (
					<li key={labelKey}>
						<Link
							className={clsx(
								'flex items-center text-sm-medium gap-space-8 p-space-4 transition-all duration-200 ease-linear text-neutral-800 hover:text-neutral-950 rounded-sm',
								isActive(link) && 'bg-white',
							)}
							onClick={onLinkClick}
							to={link}
						>
							<Icon
								className={clsx(
									'w-[32px] min-h-[32px] p-space-4 rounded-sm transition-all duration-200 ease-linear',
									isActive(link) && 'text-brand-primary-700 bg-brand-primary-50',
								)}
							/>
							{t(labelKey)}
						</Link>
					</li>
				))}
				<li key="messages">
					<Link
						className={clsx(
							'flex items-center text-sm-medium gap-space-8 p-space-4 transition-all duration-200 ease-linear text-neutral-800 hover:text-neutral-950 rounded-sm',
							isActive('/messages') && 'bg-white',
						)}
						onClick={onLinkClick}
						to="/messages"
					>
						<MessagesIcon
							className={clsx(
								'w-[32px] min-h-[32px] p-space-4 rounded-sm transition-all duration-200 ease-linear stroke-[1.5px]',
								isActive('/messages') && 'text-brand-primary-700 bg-brand-primary-50',
							)}
						/>
						{t('layout.controlPanel.messages')}
						{!!unreadThreadsCount && (
							<span className="w-space-8 h-space-8 bg-status-success-500 rounded-full" />
						)}
					</Link>
				</li>
			</menu>
			<menu className="space-y-space-4">
				{menuBottom.map(({ link, labelKey, Icon }) => (
					<li key={labelKey}>
						<Link
							className={clsx(
								'flex items-center text-sm-medium gap-space-8 p-space-4 transition-all duration-200 ease-linear text-neutral-800 hover:text-neutral-950  rounded-sm',
								isActive(link) && 'bg-white',
							)}
							onClick={onLinkClick}
							to={link}
						>
							<Icon
								className={clsx(
									'w-[32px] min-h-[32px] p-space-4 transition-all duration-200 ease-linear  rounded-sm',
									isActive(link) && 'text-brand-primary-700 bg-brand-primary-50',
								)}
							/>
							{t(labelKey)}
						</Link>
					</li>
				))}
				<LogoutMenuItem />
			</menu>
			<MenuLanguageSelector onLinkClick={onLinkClick} />
		</div>
	);
};
